<template>
  <div class="required-documents-page">
    <section class="rd-hero">
      <div class="rd-hero__glow" />
      <div class="d-flex align-items-start justify-content-between flex-wrap gap-12">
        <div class="d-flex align-items-center">
          <div class="rd-hero__icon mr-1">
            <feather-icon
              icon="FolderIcon"
              size="24"
            />
          </div>
          <div>
            <h2 class="mb-0">
              Required  Documents
            </h2>
            <p class="mb-0 rd-hero__subtitle">
              Browse engineer submissions by status and action pending documents.
            </p>
          </div>
        </div>
        <b-button
          variant="outline-primary"
          class="btn-soft-outline"
          :disabled="dataLoad"
          @click="getAllData()"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="16"
            class="mr-50"
            :class="{ 'spin-icon': dataLoad }"
          />
          Refresh
        </b-button>
      </div>
    </section>

    <div class="rd-toolbar">
      <div class="rd-search">
        <feather-icon
          icon="SearchIcon"
          size="15"
          class="rd-search__icon"
        />
        <input
          v-model="searchQuery"
          type="text"
          class="rd-search__input"
          placeholder="Search by name, email… (press Enter)"
          @keyup.enter="onSearch"
        >
      </div>
      <div class="rd-office-filter">
        <feather-icon
          icon="MapPinIcon"
          size="15"
          class="rd-office-filter__icon"
        />
        <b-form-select
          v-model="selectedOfficeId"
          :options="officeOptions"
          :disabled="officesLoading"
          value-field="value"
          text-field="text"
          class="rd-filter-select rd-office-filter__select"
          @change="onOfficeChange"
        />
      </div>
    </div>

    <div class="rd-tabs">
      <button
        v-for="tab in statusTabs"
        :key="tab.value"
        type="button"
        class="rd-tab"
        :class="[`rd-tab--${tab.value}`, { 'rd-tab--active': activeStatus === tab.value }]"
        :disabled="dataLoad"
        @click="setStatus(tab.value)"
      >
        <feather-icon
          :icon="tab.icon"
          size="16"
          class="rd-tab__icon"
        />
        <span class="rd-tab__label">{{ tab.label }}</span>
        <span
          v-if="statusCounts[tab.value] !== null"
          class="rd-tab__count"
        >
          {{ statusCounts[tab.value] }}
        </span>
      </button>
    </div>

    <div
      v-if="dataLoad"
      class="rd-loading"
    >
      <div class="rd-loading__spinner" />
      <p class="mb-0 text-muted">
        Loading {{ activeTab.label.toLowerCase() }} documents…
      </p>
    </div>

    <div
      v-else-if="!filteredUsers.length"
      class="rd-empty"
    >
      <div
        class="rd-empty__icon"
        :class="`rd-empty__icon--${activeTab.variant}`"
      >
        <feather-icon
          :icon="activeTab.icon"
          size="30"
        />
      </div>
      <h5 class="mb-50">
        No {{ activeTab.label.toLowerCase() }} documents
      </h5>
      <p class="mb-0 text-muted">
        {{ users.length ? 'Try adjusting your filters.' : `Documents marked as ${activeTab.label.toLowerCase()} will appear here.` }}
      </p>
    </div>

    <div
      v-else
      class="rd-user-list"
    >
      <article
        v-for="user in paginatedUsers"
        :key="user.id"
        class="rd-user-card"
        :class="`rd-user-card--${activeStatus}`"
      >
        <header
          class="rd-user-card__header"
          @click="toggleUser(user.id)"
        >
          <b-avatar
            :text="avatarText(user.name)"
            variant="light-primary"
            size="44px"
            class="rd-user-card__avatar"
          />
          <div class="rd-user-card__info">
            <div class="rd-user-card__name">
              {{ user.name }}
            </div>
            <div class="rd-user-card__meta">
              <span class="rd-user-card__meta-item">
                <feather-icon
                  icon="MailIcon"
                  size="13"
                />
                {{ user.email }}
              </span>
              <span
                v-if="user.office"
                class="rd-user-card__meta-item"
              >
                <feather-icon
                  icon="MapPinIcon"
                  size="13"
                />
                {{ user.office.name }}
              </span>
            </div>
          </div>
          <div class="rd-user-card__progress">
            <b-badge
              pill
              :variant="`light-${activeTab.variant}`"
            >
              <feather-icon
                :icon="activeTab.icon"
                size="12"
                class="mr-25"
              />
              {{ countDocs(user) }} {{ activeTab.label.toLowerCase() }}
            </b-badge>
          </div>
          <feather-icon
            icon="ChevronDownIcon"
            size="20"
            class="rd-user-card__chevron"
            :class="{ 'rd-user-card__chevron--open': isExpanded(user.id) }"
          />
        </header>

        <div
          v-show="isExpanded(user.id)"
          class="rd-user-card__body"
        >
          <div class="rd-doc-list">
            <div
              v-for="doc in visibleDocuments(user)"
              :key="doc.id"
              class="rd-doc-row"
              :class="`rd-doc-row--${doc.status}`"
            >
              <div
                class="rd-doc-row__icon"
                :class="`rd-doc-row__icon--${getDocumentStyle(doc.file_type).variant}`"
              >
                <feather-icon
                  :icon="getDocumentStyle(doc.file_type).icon"
                  size="18"
                />
              </div>

              <div class="rd-doc-row__content">
                <div class="rd-doc-row__type">
                  {{ formatFileType(doc.file_type) }}
                </div>
                <div class="rd-doc-row__label">
                  {{ doc.file_label }}
                </div>
                <div class="rd-doc-row__dates">
                  <span
                    v-if="doc.uploaded_at"
                    class="rd-doc-row__date"
                  >
                    <feather-icon
                      icon="UploadCloudIcon"
                      size="12"
                    />
                    Uploaded {{ formatDateTime(doc.uploaded_at) }}
                  </span>
                  <span
                    v-if="doc.reviewed_at"
                    class="rd-doc-row__date"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="12"
                    />
                    Reviewed {{ formatDateTime(doc.reviewed_at) }}
                  </span>
                </div>
                <p
                  v-if="doc.remark"
                  class="rd-doc-row__remark mb-0"
                >
                  <feather-icon
                    icon="MessageSquareIcon"
                    size="12"
                    class="mr-25"
                  />
                  {{ doc.remark }}
                </p>
                <p
                  v-if="doc.reviewed_by"
                  class="rd-doc-row__reviewer mb-0"
                >
                  Reviewed by {{ doc.reviewed_by.name }}
                </p>
                <p
                  v-if="doc.can_replace && doc.status !== 'rejected'"
                  class="rd-doc-row__reviewer mb-0"
                >
                  Waiting for the employee to upload a new copy
                </p>
              </div>

              <div class="rd-doc-row__side">
                <b-badge
                  pill
                  :variant="statusVariant(doc.status)"
                  class="rd-status-badge"
                >
                  <feather-icon
                    :icon="statusIcon(doc.status)"
                    size="12"
                    class="mr-25"
                  />
                  {{ doc.status }}
                </b-badge>

                <div class="rd-doc-row__actions">
                  <a
                    v-if="doc.drive_link"
                    :href="doc.drive_link"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="rd-action-btn rd-action-btn--view"
                    title="Open document"
                    @click.stop
                  >
                    <feather-icon
                      icon="ExternalLinkIcon"
                      size="16"
                    />
                  </a>
                  <template v-if="doc.status === 'pending'">
                    <button
                      type="button"
                      class="rd-action-btn rd-action-btn--approve"
                      title="Approve"
                      :disabled="isActionLoading(doc.id)"
                      @click.stop="confirmApprove(doc)"
                    >
                      <feather-icon
                        icon="CheckIcon"
                        size="16"
                      />
                    </button>
                    <button
                      type="button"
                      class="rd-action-btn rd-action-btn--reject"
                      title="Reject"
                      :disabled="isActionLoading(doc.id)"
                      @click.stop="confirmReject(doc)"
                    >
                      <feather-icon
                        icon="XIcon"
                        size="16"
                      />
                    </button>
                  </template>
                  <button
                    v-if="doc.can_replace === false"
                    type="button"
                    class="rd-action-btn rd-action-btn--view"
                    title="Allow the employee to upload a new copy"
                    :disabled="isActionLoading(doc.id)"
                    @click.stop="confirmAllowReupload(doc)"
                  >
                    <feather-icon
                      icon="UnlockIcon"
                      size="16"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>

    <div
      v-if="!dataLoad && totalRows > perPage"
      class="rd-pagination"
    >
      <div class="rd-pagination__info">
        Showing
        <span class="font-weight-bold">{{ rangeStart }}</span>
        -
        <span class="font-weight-bold">{{ rangeEnd }}</span>
        of
        <span class="font-weight-bold">{{ totalRows }}</span>
      </div>
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        align="center"
        first-number
        last-number
        @change="onPageChange"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import formatDate from '@/global-compositions/formatDate'

const DOCUMENT_STYLES = {
  university_degree: { icon: 'AwardIcon', variant: 'primary' },
  contracts_agreements: { icon: 'FileTextIcon', variant: 'info' },
  code_of_ethics: { icon: 'ShieldIcon', variant: 'success' },
  appointment_letter: { icon: 'MailIcon', variant: 'warning' },
  resume: { icon: 'UserIcon', variant: 'danger' },
  signed_job_descriptions: { icon: 'BriefcaseIcon', variant: 'secondary' },
  passport: { icon: 'CreditCardIcon', variant: 'info' },
  visa: { icon: 'GlobeIcon', variant: 'primary' },
  labour_card: { icon: 'BriefcaseIcon', variant: 'warning' },
}

const DEFAULT_STYLE = { icon: 'FileIcon', variant: 'primary' }

const STATUS_TABS = [
  {
    value: 'pending', label: 'Pending', hint: 'Awaiting review', icon: 'ClockIcon', variant: 'warning',
  },
  {
    value: 'approved', label: 'Approved', hint: 'Accepted documents', icon: 'CheckCircleIcon', variant: 'success',
  },
  {
    value: 'rejected', label: 'Rejected', hint: 'Declined documents', icon: 'XCircleIcon', variant: 'danger',
  },
]

export default {
  name: 'RequiredDocuments',
  data() {
    return {
      dataLoad: false,
      users: [],
      perPage: 10,
      currentPage: 1,
      totalRows: 0,
      searchQuery: '',
      selectedOfficeId: null,
      officesLoading: false,
      expandedUsers: {},
      actionLoadingIds: {},
      statusTabs: STATUS_TABS,
      activeStatus: 'pending',
      statusCounts: {
        pending: null,
        approved: null,
        rejected: null,
      },
    }
  },
  computed: {
    officeOptions() {
      return this.$store.getters['generalData/offices/offices'] || []
    },
    activeTab() {
      return this.statusTabs.find(tab => tab.value === this.activeStatus) || this.statusTabs[0]
    },
    filteredUsers() {
      return this.users.filter(user => this.countDocs(user) > 0)
    },
    paginatedUsers() {
      return this.filteredUsers
    },
    rangeStart() {
      if (!this.totalRows) return 0
      return (this.currentPage - 1) * this.perPage + 1
    },
    rangeEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalRows)
    },
  },
  watch: {
    filteredUsers() {
      this.initExpanded()
    },
  },
  async mounted() {
    if (!this.$store.getters['generalData/offices/officesLoaded']) {
      this.officesLoading = true
      try {
        await this.$store.dispatch('generalData/offices/fetchOffices')
      } finally {
        this.officesLoading = false
      }
    }
    this.getAllData(1)
  },
  methods: {
    setStatus(status) {
      if (this.activeStatus === status || this.dataLoad) return
      this.activeStatus = status
      this.getAllData(1)
    },
    onSearch() {
      this.getAllData(1)
    },
    onOfficeChange() {
      this.getAllData(1)
    },
    async getAllData(page = 1) {
      this.currentPage = page
      this.dataLoad = true
      try {
        const params = {
          skip: (page - 1) * this.perPage,
        }
        const search = this.searchQuery.trim()
        if (search) {
          params.search = search
        }
        if (this.selectedOfficeId != null && this.selectedOfficeId !== '') {
          params.office_id = this.selectedOfficeId
        }

        const response = await axios.get(`user-required-documents?status=${this.activeStatus}`, { params })
        if (response.status === 200) {
          const { data } = decryptData(response.data.payload)
          const responseData = data?.users
          this.users = Array.isArray(responseData) ? responseData : []
          this.totalRows = data?.count ?? this.users.length
          this.statusCounts[this.activeStatus] = this.totalRows
          this.initExpanded()
        }
      } catch (e) {
        this.showError(e, 'Failed to load documents')
      } finally {
        this.dataLoad = false
      }
    },
    initExpanded() {
      const next = {}
      this.paginatedUsers.forEach((user, index) => {
        next[user.id] = index === 0
      })
      this.expandedUsers = next
    },
    onPageChange(page) {
      this.getAllData(page)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    toggleUser(userId) {
      this.$set(this.expandedUsers, userId, !this.expandedUsers[userId])
    },
    isExpanded(userId) {
      return !!this.expandedUsers[userId]
    },
    visibleDocuments(user) {
      return (user.documents || []).filter(doc => doc.status === this.activeStatus)
    },
    countDocs(user) {
      return this.visibleDocuments(user).length
    },
    avatarText(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(part => part.charAt(0))
        .join('')
        .slice(0, 2)
        .toUpperCase()
    },
    getDocumentStyle(fileType) {
      return DOCUMENT_STYLES[fileType] || DEFAULT_STYLE
    },
    formatFileType(fileType) {
      if (!fileType) return 'Document'
      return String(fileType)
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    formatDateTime(value) {
      return formatDate(value) || value
    },
    statusVariant(status) {
      if (status === 'approved') return 'light-success'
      if (status === 'rejected') return 'light-danger'
      return 'light-warning'
    },
    statusIcon(status) {
      if (status === 'approved') return 'CheckCircleIcon'
      if (status === 'rejected') return 'XCircleIcon'
      return 'ClockIcon'
    },
    isActionLoading(docId) {
      return !!this.actionLoadingIds[docId]
    },
    setActionLoading(docId, loading) {
      this.$set(this.actionLoadingIds, docId, loading)
    },
    confirmApprove(doc) {
      this.$swal({
        title: 'Approve this document?',
        text: doc.file_label,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Approve',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#28c76f',
        cancelButtonColor: '#82868b',
        width: 520,
      }).then(result => {
        if (result.isConfirmed) {
          this.submitReview(doc, 'approved')
        }
      })
    },
    confirmReject(doc) {
      this.$swal({
        title: 'Reject this document?',
        text: doc.file_label,
        icon: 'warning',
        input: 'textarea',
        inputPlaceholder: 'Enter remark (required)',
        inputAttributes: {
          'aria-label': 'Remark',
          required: 'required',
        },
        showCancelButton: true,
        confirmButtonText: 'Reject',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#ea5455',
        cancelButtonColor: '#82868b',
        width: 560,
        preConfirm: remark => {
          if (!remark || !String(remark).trim()) {
            this.$swal.showValidationMessage('Remark is required to reject this document')
            return false
          }
          return String(remark).trim()
        },
      }).then(result => {
        if (result.isConfirmed) {
          this.submitReview(doc, 'rejected', result.value)
        }
      })
    },
    confirmAllowReupload(doc) {
      this.$swal({
        title: 'Allow a new copy?',
        text: `${doc.file_label} stays as it is until the employee uploads a replacement, `
          + 'which will come back here as pending.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Allow',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.allowReupload(doc)
        }
      })
    },
    async allowReupload(doc) {
      this.setActionLoading(doc.id, true)
      try {
        const response = await axios.put(`user-required-documents/${doc.id}/allow-reupload`)
        if (response.status === 200) {
          this.$set(doc, 'can_replace', true)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'The employee can now replace this document',
              icon: 'UnlockIcon',
              variant: 'success',
            },
          })
        }
      } catch (e) {
        this.showError(e, 'Could not allow a new upload')
      } finally {
        this.setActionLoading(doc.id, false)
      }
    },
    async submitReview(doc, status, remark = '') {
      this.setActionLoading(doc.id, true)
      try {
        const payload = { status }
        if (status === 'rejected') {
          payload.remark = remark
        }

        const response = await axios.put(`user-required-documents/${doc.id}`, payload)
        if (response.status === 200) {
          this.removeLocalDocument(doc.id)
          if (this.activeStatus === 'pending' && this.totalRows > 0) {
            this.totalRows -= 1
          }
          if (this.statusCounts.pending !== null) {
            this.statusCounts.pending = Math.max(0, this.statusCounts.pending - 1)
          }
          this.statusCounts[status] = null
          this.$toast({
            component: ToastificationContent,
            props: {
              title: status === 'approved' ? 'Document approved' : 'Document rejected',
              icon: status === 'approved' ? 'CheckIcon' : 'XIcon',
              variant: 'success',
            },
          })
        }
      } catch (e) {
        this.showError(e, 'Review action failed')
      } finally {
        this.setActionLoading(doc.id, false)
      }
    },
    removeLocalDocument(docId) {
      this.users = this.users
        .map(user => ({
          ...user,
          documents: (user.documents || []).filter(doc => doc.id !== docId),
        }))
    },
    showError(error, fallback) {
      let message = fallback
      try {
        if (error?.response?.data?.payload) {
          const payload = decryptData(error.response.data.payload)
          const msg = payload?.message || payload
          if (typeof msg === 'string') message = msg
          else if (typeof msg === 'object') {
            const firstList = Object.values(msg).find(Array.isArray)
            if (firstList) {
              const [firstMessage] = firstList
              if (firstMessage) message = firstMessage
            }
          }
        }
      } catch {
        // keep fallback
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: 'BellIcon',
          variant: 'danger',
        },
      })
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/pages/user-site/required-documents.scss"></style>
